import type { Organization } from '@local/common-lib';
import { ArrowBack as CancelIcon, Save as SaveIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { CardActions, CardContent, Grid, Typography } from '@mui/material';
import type { FC } from 'react';
import { FormikChipList } from '../../components/formik-fields/chip-list.js';
import { FormikSwitch } from '../../components/formik-fields/switch.js';
import { FormikTextField } from '../../components/formik-fields/text-field.js';
import { CenterCard } from '../../components/page-layout/center-card.js';
import type { EditViewProps } from '../../utils/types.js';

export const EditView: FC<EditViewProps<Organization.Patch>> = ({
  cancel,
  formik,
}) => {
  return (
    <form onReset={formik.handleReset} onSubmit={formik.handleSubmit}>
      <CenterCard>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                id="name"
                label="Organization Name"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                id="policeStation"
                label="Police Station"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5">Features</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikSwitch
                formik={formik}
                helperText="Enable patrollers to create their own patrols with 30 minutes to 12 hours advance notice. Patrollers will be automatically assigned and finalized when they create a quick patrol."
                id="allowQuickPatrol"
                label="Allow Quick Patrol"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5">Call Signs</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikChipList
                formik={formik}
                helperText="Call signs available when creating a patrol. Type a call sign and press Enter to add it."
                id="callSigns"
                label="Call Signs"
                placeholder="Add call sign"
                transform={(value) => value.toLocaleUpperCase()}
              />
            </Grid>
            <Grid item xs={12}>
              <FormikSwitch
                formik={formik}
                helperText="Only allow the call signs listed above to be used when creating a patrol."
                id="restrictCallSigns"
                label="Restrict to listed call signs"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5">Patrol History</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikSwitch
                formik={formik}
                helperText="Hide the 'NOT STARTED' badge on past patrols that were never recorded as started."
                id="hideUnstartedPatrolsBadge"
                label="Hide unstarted badge on past patrols"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h5">Email Configuration</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="The email address that emails are sent from."
                id="emailFrom"
                label="Email From"
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="Optional email address to CC when Police ECC replies to patrol messages."
                id="eccReplyCcEmail"
                label="CC for Police ECC Replies"
                placeholder="Name <email@address.com>"
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="Instructions for where and how to meet up for the patrol. These will be sent to patrollers and guests."
                id="patrolMeetUpInstructions"
                label="Patrol Meetup Instructions"
                minRows={4.5}
                multiline
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                helperText="Instructions and reminders for the patrol. These will be sent to patrollers only."
                id="patrollerInstructions"
                label="Patroller Instructions"
                minRows={4.5}
                multiline
              />
            </Grid>
            <Grid item xs={12}>
              <FormikTextField
                formik={formik}
                id="emailSignOff"
                label="Email Sign Off"
                minRows={2.5}
                multiline
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', pb: 3 }}>
          <LoadingButton
            color="primary"
            loading={formik.isSubmitting}
            onClick={cancel}
            startIcon={<CancelIcon />}
          >
            Cancel
          </LoadingButton>
          <LoadingButton
            color="primary"
            loading={formik.isSubmitting}
            startIcon={<SaveIcon />}
            type="submit"
            variant="contained"
          >
            Save
          </LoadingButton>
        </CardActions>
      </CenterCard>
    </form>
  );
};
